import {Button, Box, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import React, {useMemo, useState} from 'react'

import AspectRatio from '../aspect-ratio'
import CTA from '../cta'
import {hasValidImageSrc, hasValidString, isVideoAsset} from '../../../utils/utils'
import teaserSlotRecipe from './index.recipe'
import {ArrowReloadHorizontalIcon} from '../../icons'
import {FlipAnimation} from '../flip-animation'
import Asset from '../asset'
import {useIntl} from 'react-intl'
import RichText from '../rich-text'

/**
 * Renders a teaser component with media, text content, and optional call-to-action.
 * Intended for use within the Leap components library.
 */

const Teaser = ({
    asset,
    assetFlip,
    headline,
    hideHeadline,
    copy,
    cta,
    aspectRatio,
    loading = 'lazy',
    widths,
    teaserBackground,
    variant = 'card',
    headlineId = undefined,
    textSpacing,
    trackingContext,
    headingLevel,
    flipButtonVariant = 'mediaGhost'
}) => {
    const {formatMessage} = useIntl()
    const [flipped, setFlipped] = useState(false)

    const recipe = useSlotRecipe({recipe: teaserSlotRecipe})
    const styles = useMemo(
        () => recipe({teaserBackground, textSpacing}),
        [recipe, teaserBackground, textSpacing]
    )

    const hasCopy = hasValidString(copy.text)
    const hasHeadline = hasValidString(headline)
    const hasCtaLabel = hasValidString(cta?.label)

    const hasContent = hasCopy || hasCtaLabel
    const isHeadlineElement = hasHeadline && hasContent && headingLevel !== undefined

    const headlineElement = isHeadlineElement ? `h${headingLevel + 1}` : 'p'

    const videoRef = React.useRef(null)

    const front = (
        <Asset
            asset={asset}
            videoRef={videoRef}
            loading={loading}
            objectFit="cover"
            widths={widths}
            variant="teaser"
        />
    )

    return (
        <>
            {variant === 'card' && (
                <Box
                    css={styles.root}
                    role="group"
                    aria-label={formatMessage({
                        defaultMessage: 'article',
                        id: 'global.article'
                    })}
                >
                    {!!asset && (
                        <AspectRatio ratio={aspectRatio}>
                            <Box css={styles.imageContainer}>
                                {hasValidImageSrc(assetFlip) ? (
                                    <FlipAnimation
                                        isFlipped={flipped}
                                        front={front}
                                        back={
                                            <Asset
                                                asset={assetFlip}
                                                objectFit="cover"
                                                widths={widths}
                                                loading="lazy" // always lazy as it's not initially visible
                                            />
                                        }
                                    />
                                ) : (
                                    front
                                )}

                                {hasValidImageSrc(assetFlip) && !isVideoAsset(asset) && (
                                    <Button
                                        css={styles.iconButton}
                                        variant={flipButtonVariant}
                                        size="md"
                                        aria-label={formatMessage(
                                            {
                                                id: 'leap.teaser.assistive_msg.flip',
                                                defaultMessage: 'Show Image {nextFlipHeadline}'
                                            },
                                            {nextFlipHeadline: flipped ? asset.alt : assetFlip.alt}
                                        )}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setFlipped(!flipped)
                                        }}
                                    >
                                        <ArrowReloadHorizontalIcon />
                                    </Button>
                                )}

                                {hasValidImageSrc(assetFlip) && !isVideoAsset(asset) && (
                                    <Box
                                        position="absolute"
                                        bottom="0"
                                        left="0"
                                        right="0"
                                        height="10.219rem"
                                        bg="gradient.bottom.darken-40"
                                        pointerEvents="none"
                                    />
                                )}
                            </Box>
                        </AspectRatio>
                    )}

                    <Box css={styles.container}>
                        <Box css={styles.textContainer}>
                            {hasHeadline && (
                                <RichText
                                    as={headlineElement}
                                    id={headlineId}
                                    className={hideHeadline ? 'sr-only' : undefined}
                                    css={styles.headline}
                                >
                                    {headline}
                                </RichText>
                            )}
                            {hasCopy && <RichText css={styles.copy}>{copy.text}</RichText>}
                        </Box>
                        {hasCtaLabel && <CTA {...cta} trackingContext={trackingContext} />}
                    </Box>
                </Box>
            )}
        </>
    )
}

Teaser.propTypes = {
    asset: PropTypes.object.isRequired,
    assetFlip: PropTypes.object,
    aspectRatio: PropTypes.number,
    headline: PropTypes.string.isRequired,
    hideHeadline: PropTypes.bool,
    copy: PropTypes.object,
    cta: PropTypes.object,
    headingLevel: PropTypes.number,
    loading: PropTypes.bool,
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    teaserBackground: PropTypes.bool,
    variant: PropTypes.string,
    textSpacing: PropTypes.string,
    headlineId: PropTypes.string,
    trackingContext: PropTypes.shape({
        moduleName: PropTypes.string
    }),
    flipButtonVariant: PropTypes.string
}

export default Teaser
